import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

const OtpInput = ({
  numInputs,
  onChange,
  containerStyle,
  inputStyle,
  inputType = 'number',
  shouldAutoFocus = true,
  value = ''
}) => {
  const [otp, setOtp] = useState(
    value ? value.toString().split('') : Array(numInputs).fill('')
  );

  const inputRefs = Array(numInputs)
    .fill(0)
    .map(() => useRef(null));

  useEffect(() => {
    if (shouldAutoFocus && inputRefs) {
      inputRefs[0].current.focus();
    }
  }, [shouldAutoFocus, inputRefs.current]);

  useEffect(() => {
    // Reset the otp values
    if (!value) {
      setOtp(Array(numInputs).fill(''));
    }
  }, [value]);

  const isInputNum = inputType === 'number' || inputType === 'tel';

  const isInputValueValid = value => {
    const isTypeValid = isInputNum
      ? !isNaN(Number(value))
      : typeof value === 'string';

    return isTypeValid && value.trim().length === 1;
  };

  const handleChange = (e, index) => {
    const value = e.target.value;

    if (isInputValueValid(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      onChange(newOtp.join(''));

      if (index + 1 < numInputs && newOtp[index] !== '') {
        inputRefs[index + 1].current.removeAttribute('disabled');
        inputRefs[index + 1].current.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      return;
    }

    // Move to the previous input on pressing backspace
    if (e.key === 'Backspace') {
      e.preventDefault();

      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);

      if (inputRefs[index - 1]) {
        inputRefs[index - 1].current.removeAttribute('disabled');

        inputRefs[index - 1].current.focus();
      }
    } else if (
      e.key === 'ArrowRight' &&
      index < numInputs - 1 &&
      otp[index] !== ''
    ) {
      inputRefs[index + 1].current.removeAttribute('disabled');
      inputRefs[index + 1].current.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs[index - 1].current.focus();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text/plain')
      .slice(0, numInputs - index);

    const newOtp = [...otp];
    for (let i = index, j = 0; j < pastedData.length; i++, j++) {
      newOtp[i] = pastedData[j];

      inputRefs[i].current.removeAttribute('disabled');
    }

    setOtp(newOtp);
    onChange(newOtp.join(''))
  };

  return (
    <div className={containerStyle}>
      {otp.map((value, index) => (
        <input
          className={inputStyle}
          key={index}
          ref={inputRefs[index]}
          maxLength="1"
          value={value}
          onChange={e => handleChange(e, index)}
          onKeyDown={e => handleKeyDown(e, index)}
          disabled={index !== 0 && otp[index - 1] === ''}
          onPaste={e => handlePaste(e, index)}
          type={inputType}
          aria-label={`Please enter OTP character ${index + 1}`}
        />
      ))}
    </div>
  );
};

OtpInput.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // |     To update them edit the d.ts file and run "yarn proptypes"     |
  // ----------------------------------------------------------------------
  /**
   * OTP input container style
   */
  containerStyle: PropTypes.string,
  /**
   * Default prefilled values for the input
   */
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * OTP input element style
   */
  inputStyle: PropTypes.string,
  /**
   * Input Types for OTP Input
   * @default false
   */
  inputType: PropTypes.oneOf(['number', 'tel', 'text']),
  /**
   * Number of digits in Pin input
   */
  numInputs: PropTypes.oneOf([4, 6]).isRequired,
  /**
   * Change handler
   * @param value New value of the field
   */
  onChange: PropTypes.func,
  /**
   * Auto focuses input on initial page load.
   * @default true
   */
  shouldAutoFocus: PropTypes.bool,
  /**
   * value prop for the field.
   * Used to render values in fully controlled state.
   */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

OtpInput.defaultProps = {
  containerStyle: '',
  inputStyle: '',
  shouldAutoFocus: true,
  value: ''
};

export default OtpInput;
